import './index.scss';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Modal, UmuSelect } from '@umu/umu-design';
import { ModalFuncProps } from '@umu/design-base';
import { lang } from '@umu/ukit-lang';
export type { IPermissionSettingDataInfo } from '../../interface';
export type { IPermissionSettingParams } from '../index';
import { IClosePermissionInfo, IPermissionSettingDataInfo, PermissionType, PlatePermissionType } from '../../interface';
import { defaultPermissionDataInfo, shareOptionMap } from '../../dataFlow';
import { getPermissionList } from '@umu/umu-api';
import { ISearchItem, SearchInput } from '../../../searchInput';
import { PermissionMemberList } from '../memberList';
import { onTransformParams } from '../permissionContentInfo/dataFlow';
import { searchAccount } from '@umu/umu-api';
import { IPermissionSettingParams, PermissionSettingPageType } from '@umu/umu-api';
import { setSeAccountPermission, setCancelPermissionAll } from '../../server';
import { onTransformMenuList } from '../memberInfo';
import { IAccountInfo } from '../../interface/account';
import { If, Loading, IfShow } from 'common/src/components';
import { Account } from '../../../../@utils/normalizeAccount';

const { confirm } = Modal;

interface IPermissionSettingInfo {
    id: string;
}
interface IPermissionSettingProps extends React.HTMLAttributes<HTMLDivElement> {
    title: string;
    subTitle?: string;
    prefixCls?: string; // 样式前缀
    platformPermission?: PlatePermissionType; // 平台权限
    prefixElement?: React.ReactNode | string; // 前置元素
    suffixElement?: React.ReactNode | string; // 后置元素
    isHideFooter?: boolean; // 是否隐藏底部
    params: IPermissionSettingParams;
    okProps?: any; // 确定按钮属性
    okText?: string; // 确定按钮文案
    cancelText?: string; // 取消按钮文案
    pageType?: PermissionSettingPageType; // 页面类型
    isCloseInitLoading?: boolean; // 是否关闭初始化加载数据
    initPermission?: boolean;
    normalizeAccountEPU?: (account: Account) => string;
    normalizeAccountName?: (account: Account) => string;
    onLoadPermissionSetting?: (
        pageType?: PermissionSettingPageType,
        params?: IPermissionSettingParams,
    ) => Promise<IPermissionSettingInfo>; // 加载导航回调
    onClose?(): void; // 关闭弹窗回调
    onUpdate?: (data: IPermissionSettingInfo) => void; // 更新页面信息回调函数
    onSave?: (data: IPermissionSettingInfo, params?: IPermissionSettingParams) => Promise<{ isSuccess: boolean }>; // 保存按钮回调函数
    onSuccess?: (permission: PermissionType) => void; // 配置成功回调函数
    onSearchMember?: (key: string) => Promise<ISearchItem[]>;
}

const PermissionSettingPage = (props: IPermissionSettingProps) => {
    const {
        prefixCls = 'global-permission-setting-page',
        okText = '',
        subTitle = '',
        isCloseInitLoading = false,
        suffixElement,
        prefixElement,
        cancelText = '',
        okProps = {},
        platformPermission,
        isHideFooter = false,
        initPermission = false,
        params,
        onClose,
        onSuccess,
        normalizeAccountEPU,
        normalizeAccountName,
    } = props;
    const [isLoad, setIsLoad] = useState(true);
    const [dataInfo, setDataInfo] = useState<IPermissionSettingDataInfo>(defaultPermissionDataInfo);
    const [closeInfo, setCloseInfo] = useState<IClosePermissionInfo>({
        isAll: false,
        list: [],
    });
    const [accountSelectList, setAccountSelectList] = useState<ISearchItem[]>([]);

    const onUpdateDataInfo = (data: IPermissionSettingDataInfo) => {
        setDataInfo({
            ...dataInfo,
            ...data,
        });
    };
    const initDataList = () => {
        Promise.all([
            getPermissionList({
                obj_id: params.id,
                obj_type: params.obj_type,
                se_id: params.se_id || '',
            }),
        ]).then(([res]) => {
            const { list = [] } = res;
            let selected = res.selected;
            const isUseAndUmuPlate = Number(platformPermission) === PlatePermissionType.UMU_AND_USE;
            const permissionList = list.map((item) => {
                let label = shareOptionMap[(item as PermissionType) || PermissionType.__INIT__].label;
                if (isUseAndUmuPlate && String(item) === PermissionType.PUBLIC_IN_THE_COMPANY) {
                    label = shareOptionMap[PermissionType.USE_PLATE].label;
                }
                return {
                    label,
                    value: item,
                };
            });

            if (initPermission) {
                selected = list[0];
            }

            onUpdateDataInfo({
                ...dataInfo,
                permissionList,
                isLoading: false,
                permissionValue: selected as PermissionType,
            });
            isLoad && setIsLoad(false);
        });
    };

    // 保存按钮文字信息
    const okTextName = useMemo(() => {
        return okText || lang('完成');
    }, [okText]);

    // 取消按钮文字
    const cancelTextName = useMemo(() => {
        return cancelText || lang('取消');
    }, [cancelText]);

    const onSubmit = async () => {
        const accountList = accountSelectList || [];
        const permissionValueChange = dataInfo.permissionValue !== dataInfo.navInfo.access_permission;
        const isClearPermission = dataInfo.permissionValue === PermissionType.DESIGNATED_ACCOUNT && closeInfo.isAll;
        const isAccountChange =
            dataInfo.permissionValue === PermissionType.DESIGNATED_ACCOUNT &&
            (accountList.length > 0 || closeInfo.list.length > 0);
        if (permissionValueChange) {
            onSuccess?.(dataInfo.permissionValue);
        }
        if (isClearPermission) {
            await setCancelPermissionAll({
                obj_id: params.id || '',
                obj_type: params.obj_type,
            });
        }
        if (isAccountChange) {
            const list = onTransformParams(accountList, closeInfo.list);
            await setSeAccountPermission({
                obj_id: params.id || '',
                obj_type: params.obj_type,
                se_id: params.se_id || '',
                accounts: JSON.stringify(list),
            });
        }
        onClose?.();
    };

    useEffect(() => {
        if (!isCloseInitLoading) {
            initDataList();
        } else {
            setIsLoad(false);
        }
    }, []);
    const onPermissionChange = (item: any) => {
        onUpdateDataInfo({
            ...dataInfo,
            permissionValue: item as PermissionType,
        });
    };
    const onLoadSearchData = (value: string) => {
        if (value.trim() === '') {
            return Promise.resolve([]);
        }
        return searchAccount({ accounts: value, se_id: params.se_id, search_source: 'se_practice' }).then(
            (users: ISearchItem[]) => {
                return users;
            },
        );
    };
    const onPreviewLoadData = (value: string) => {
        if (value.trim() === '') {
            return Promise.resolve([]);
        }
        return searchAccount({ accounts: value, se_id: params.se_id, search_source: 'se_practice' }).then(
            (users: ISearchItem[]) => {
                return onTransformMenuList(users as any[]);
            },
        );
    };
    const onUserListChange = (data: ISearchItem[], _isDel?: boolean) => {
        setAccountSelectList(data);
    };

    const onCancelPermissionAll = () => {
        setCloseInfo({
            isAll: true,
            list: [],
        });
    };
    const onCancelPermission = (item: IAccountInfo) => {
        setCloseInfo({
            isAll: false,
            list: [...closeInfo.list, item],
        });
    };

    const renderPermissionSelect = () => {
        return (
            <div className={`${prefixCls}-content-permission`}>
                <If condition={dataInfo.permissionList.length > 0}>
                    <UmuSelect
                        width="100%"
                        value={dataInfo.permissionValue}
                        className={`${prefixCls}-content-permission-list`}
                        dropdownClassName={`${prefixCls}-content-permission-dropdown`}
                        options={dataInfo.permissionList}
                        onChange={onPermissionChange}
                    />
                </If>
                <IfShow show={dataInfo.permissionValue === PermissionType.DESIGNATED_ACCOUNT}>
                    <Fragment>
                        <div className={`${prefixCls}-content-permission-search-member`}>
                            <SearchInput
                                preTitle={lang('以下人员可见：')}
                                isSePlate
                                useId={params.se_id || ''}
                                className={`${prefixCls}-permission-search`}
                                placeholder={lang('请输入账户名或部门名，用逗号分隔')}
                                isAllowShowBatch
                                isAllowShowDepartment
                                batchProps={{
                                    previewPlaceholder: lang(
                                        '请输入成员账户信息（邮箱、手机号、用户名）、部门名，用逗号或回车分隔，输入完成后点击下方“预览”按钮查看。',
                                    ),
                                }}
                                onTransformMenuList={onTransformMenuList as any}
                                onPreviewLoadData={onPreviewLoadData}
                                onLoadData={onLoadSearchData}
                                onSelectChange={onUserListChange}
                            />
                        </div>
                        <div className={`${prefixCls}-content-permission-member-list`}>
                            <PermissionMemberList
                                id={params.id || ''}
                                isModeView={
                                    (String(dataInfo.permissionValue) as PermissionType) ===
                                    PermissionType.PUBLIC_IN_THE_COMPANY
                                }
                                seId={params.se_id || ''}
                                sourceType={params.obj_type}
                                closeList={closeInfo.list}
                                normalizeAccountEPU={normalizeAccountEPU}
                                onCancelPermission={onCancelPermission}
                                normalizeAccountName={normalizeAccountName}
                                onCancelPermissionAll={onCancelPermissionAll}
                            />
                        </div>
                    </Fragment>
                </IfShow>
            </div>
        );
    };

    const renderNavContent = () => {
        if (dataInfo.isLoading) {
            return null;
        }
        return (
            <div className={`${prefixCls}-content`}>
                {subTitle && <div className={`${prefixCls}-content-heard`}>{subTitle}</div>}
                {renderPermissionSelect()}
            </div>
        );
    };

    return (
        <div className={`${prefixCls}`} onClick={(e) => e.stopPropagation()}>
            <If condition={!!prefixElement}>
                <div className={`${prefixCls}-heard`}>{prefixElement}</div>
            </If>
            <If condition={dataInfo.isLoading}>
                <Loading />
            </If>
            {renderNavContent()}
            <If condition={!!suffixElement}>
                <div className={`${prefixCls}-suffix`}>{suffixElement}</div>
            </If>
            <If condition={!isHideFooter}>
                <div className={`${prefixCls}-footer`}>
                    <Button type="ghost-grey" className={`${prefixCls}-footer-col-split`} onClick={onClose}>
                        {cancelTextName}
                    </Button>
                    <Button type="primary-blue" onClick={onSubmit} {...okProps}>
                        {okTextName}
                    </Button>
                </div>
            </If>
        </div>
    );
};

export const openPermissionSettingModal = (data: IPermissionSettingProps, dialogInfo?: ModalFuncProps) => {
    const { ...rest } = dialogInfo || {};

    const dialog = confirm({
        title: data.title,
        centered: true,
        width: '8.6rem',
        footer: null,
        zIndex: 1000,
        className: 'global-permission-setting-page-modal',
        ...rest,
        content: (
            <PermissionSettingPage
                {...data}
                onClose={() => {
                    data?.onClose?.();
                    dialog.destroy();
                }}
            />
        ),
    } as any);
    return dialog;
};
